import {
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {pinIssueMutation, pinIssueMutation$data} from './__generated__/pinIssueMutation.graphql'

/** Packages an issue mutation response into the `{id, values}` shape the event carries, or
 * `undefined` if the affected node is missing (e.g. an unexpectedly null payload field).
 * Includes repository `pinnedIssues.totalCount` when the server returns it. */
function toIssuePatch(
  issueId: string,
  repositoryId: string | undefined,
  data: pinIssueMutation$data,
): ObjectPropertiesPatch | undefined {
  const issue = data.pinIssue?.issue
  if (issue?.isPinned == null) return undefined

  const totalCount = issue.repository?.pinnedIssues?.totalCount
  const repositoryValues =
    repositoryId !== undefined && totalCount != null
      ? {repository: {id: repositoryId, pinnedIssues: {totalCount}}}
      : undefined

  return {id: issueId, values: {isPinned: issue.isPinned, ...repositoryValues}}
}

export function commitPinIssueMutation({
  environment,
  input,
  repositoryId,
  currentPinnedCount,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {issueId: string}
  repositoryId?: string
  currentPinnedCount?: number
  onCompleted?: () => void
  onError?: (error: Error) => void
}) {
  const optimisticResponse =
    repositoryId !== undefined && currentPinnedCount !== undefined
      ? {
          pinIssue: {
            issue: {
              id: input.issueId,
              isPinned: true,
              repository: {
                id: repositoryId,
                pinnedIssues: {
                  totalCount: currentPinnedCount + 1,
                },
              },
            },
          },
        }
      : undefined

  // Build a single optimistic patch for the issue node. When repositoryId and
  // currentPinnedCount are available the patch also includes the repository's
  // new pinnedIssues count so subscribers can update both in one pass.
  const optimisticRepositoryValues =
    repositoryId !== undefined && currentPinnedCount !== undefined
      ? {repository: {id: repositoryId, pinnedIssues: {totalCount: currentPinnedCount + 1}}}
      : undefined
  const optimisticPatch: ObjectPropertiesPatch = {
    id: input.issueId,
    values: {isPinned: true, ...optimisticRepositoryValues},
  }
  const optimisticEvent = new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
    phase: 'optimistic',
    data: optimisticPatch,
  })
  const mutationId = optimisticEvent.detail.mutationId

  const disposable = commitMutation<pinIssueMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation pinIssueMutation($input: PinIssueInput!) @raw_response_type {
        pinIssue(input: $input) {
          issue {
            isPinned
            repository {
              pinnedIssues(first: 3) {
                totalCount
              }
            }
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticResponse,
    onCompleted: response => {
      const patch = toIssuePatch(input.issueId, repositoryId, response)
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      onCompleted?.()
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      onError?.(error)
    },
  })

  // Relay applies `optimisticResponse` synchronously as part of `commitMutation`,
  // so it's safe to dispatch the optimistic phase event right after committing.
  dispatchMutationLifecycleEvent(optimisticEvent)

  return disposable
}
